import { FormikProps } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PostalCodeAndCityInput, { PostalCodeAndCityInputProps } from './PostalCodeAndCityInput';
import { FormikTextField } from './FormikTextField';
import { Stack } from '@mediahuis/chameleon-react';
import { splitPostalCodeAndCity } from '../../utils/formatting';

export type RequiredFormikValues = {
  address: {
    city?: string;
    postalCode?: string;
  };
};
type FormikPostalCodeAndCityInputProps<T extends RequiredFormikValues> = Pick<
  PostalCodeAndCityInputProps,
  'countryISO' | 'style'
> & {
  formik: FormikProps<T>;
  autoComplete: boolean;
};

export const FormikPostalCodeAndCityInput = <T extends RequiredFormikValues>({
  formik,
  autoComplete,
  ...props
}: FormikPostalCodeAndCityInputProps<T>) => {
  const [inputValue, setInputValue] = React.useState('');
  const { t } = useTranslation();
  // The type of Formik is incorrect which causes Typescript to not narrow the type
  const { values, errors, touched, setTouched, setValues, setFieldValue } =
    formik as unknown as FormikProps<RequiredFormikValues>;

  const validationMessage = useMemo(() => {
    const isTouched = touched.address?.city && touched.address?.postalCode;
    const combinedErrorMessage =
      errors.address?.postalCode &&
      !values.address.postalCode &&
      errors.address.city &&
      !values.address.city
        ? 'register.error.requiredAddress'
        : undefined;
    const errorMessage = combinedErrorMessage || errors.address?.postalCode || errors.address?.city;
    if (isTouched && errorMessage) {
      return t(errorMessage);
    }
    return undefined;
  }, [
    touched.address?.city,
    touched.address?.postalCode,
    values.address.postalCode,
    values.address.city,
    errors.address?.postalCode,
    errors.address?.city,
    t,
  ]);

  const onChange = useCallback(
    (input: string) => {
      setInputValue(input);
      setValues((prevState) => {
        const value = splitPostalCodeAndCity(input);
        return {
          ...prevState,
          address: {
            ...values.address,
            city: value.city,
            postalCode: value.postalCode,
          },
        };
      });
    },
    [values, setValues]
  );

  const onBlur = useCallback(() => {
    if (!touched.address?.city || !touched.address.postalCode) {
      setTouched({ ...touched, address: { ...touched.address, postalCode: true, city: true } });
    }
  }, [touched, setTouched]);

  const postalCodeAndCityInputValue = useMemo((): string | undefined => {
    if (values.address.postalCode && values.address.city) {
      return `${values.address.postalCode} ${values.address.city}`;
    }
    return inputValue;
  }, [values.address.postalCode, values.address.city, inputValue]);

  return (
    <>
      {autoComplete && (
        <PostalCodeAndCityInput
          {...props}
          value={postalCodeAndCityInputValue}
          onChange={onChange}
          onBlur={onBlur}
          label={t('register.address.postalCodeAndCity')}
          validationMessage={validationMessage}
        />
      )}
      {!autoComplete && (
        <Stack
          className="g-5"
          flexDirection={{
            sm: 'row',
            md: 'row',
            lg: 'row',
            xl: 'row',
          }}
          flexWrap={'nowrap'}>
          <FormikTextField
            name="address.postalCode"
            id="address.city"
            formikProps={formik}
            label={t('register.address.postalCode')}
            data-cy="addressPostalCode"
          />
          <FormikTextField
            name="address.city"
            id="address.city"
            formikProps={formik}
            label={t('register.address.city')}
            data-cy="addressCity"
          />
        </Stack>
      )}
    </>
  );
};
